import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  FileArchive,
  Landmark,
  List,
  Search,
  Undo2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import {
  SepaDirectionEnum,
  SepaTransactionStatusEnum,
} from "../../../../../stores/Sepa/sepa.types";
import SepaTransactionTab from "./SepaTransactionTab";
import SepaExportBatchTab from "./SepaExportBatchTab";
import SepaExportBatchTimelineDialog from "./SepaExportBatchTimelineDialog";
import SepaSpotlightSearch from "./SepaSpotlightSearch";

/** The sections of the SEPA export manager. */
type SepaTab = "lastschrift" | "rueckerstattungen" | "zurueckgewiesen" | "alle" | "exporte";

/**
 * Self-contained SEPA export manager (shadcn) rendered from the "SEPA LASTSCHRIFT"
 * tab in TransactionPage. Owns its own Mandant (account) selector, tabs and — in
 * later phases — cursor-paginated tables, filters and the export/undo flows.
 *
 * F2.0: shell only (account selector + tabs). The tab bodies are filled in F2.1+.
 */
const SepaExportManager: React.FC = () => {
  const userAccounts = useUserAccounts();
  const accounts = useMemo(() => userAccounts ?? [], [userAccounts]);

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<SepaTab>("lastschrift");
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  // The export whose lifecycle timeline is open (from the Exporte or Zurück tabs).
  const [timelineBatchId, setTimelineBatchId] = useState<string | null>(null);
  // The batch to scroll to + flash in the Exporte tab after navigating from a
  // transaction's export chip; nonce bumps so re-clicking the same batch re-flashes.
  const [highlightBatch, setHighlightBatch] = useState<{ id: string; nonce: number } | null>(null);

  const openBatchInExporte = (batch: { id: string }) => {
    setActiveTab("exporte");
    setHighlightBatch({ id: batch.id, nonce: Date.now() });
  };

  // Default to the first available account (Mandant) once accounts load.
  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  const selectedAccountName = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId)?.name,
    [accounts, selectedAccountId],
  );

  // Folder-style tabs sitting on an underline; active uses the "Rechnung erstellen" maroon.
  const tabTriggerClass =
    "group -mb-px inline-flex h-10 cursor-pointer items-center gap-2 rounded-t-lg border border-b-0 " +
    "border-solid border-slate-200 bg-slate-100 px-4 text-sm font-semibold text-slate-600 " +
    "shadow-none transition-colors hover:bg-slate-200 data-[state=active]:border-[#8a4150] " +
    "data-[state=active]:bg-gradient-to-b data-[state=active]:from-[#a5515f] " +
    "data-[state=active]:to-[#8a4150] data-[state=active]:text-white data-[state=active]:shadow-none";

  return (
    <Card className="overflow-hidden rounded-b-2xl rounded-t-none border border-solid border-black shadow-none">
      <CardHeader className="bg-slate-100 py-4">
        <CardTitle className="flex items-center justify-center gap-2 text-lg font-semibold text-slate-800">
          <Landmark className="h-5 w-5 text-slate-500" />
          SEPA-Lastschrift &amp; Export
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-2">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SepaTab)}>
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <TabsList className="flex h-auto w-auto items-end gap-1.5 rounded-none bg-transparent p-0">
              <TabsTrigger value="lastschrift" className={tabTriggerClass}>
                <ArrowDownToLine className="h-4 w-4" />
                Lastschrift
              </TabsTrigger>
              <TabsTrigger value="rueckerstattungen" className={tabTriggerClass}>
                <ArrowUpFromLine className="h-4 w-4" />
                Rückerstattungen
              </TabsTrigger>
              <TabsTrigger value="zurueckgewiesen" className={tabTriggerClass}>
                <Undo2 className="h-4 w-4" />
                Zurückgewiesen
              </TabsTrigger>
              <TabsTrigger value="alle" className={tabTriggerClass}>
                <List className="h-4 w-4" />
                Alle Transaktionen
              </TabsTrigger>
              <TabsTrigger value="exporte" className={tabTriggerClass}>
                <FileArchive className="h-4 w-4" />
                Exporte
              </TabsTrigger>
            </TabsList>

            {accounts.length > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="outline"
                  className="h-10 gap-2 border-solid border-slate-300 text-sm text-slate-600 hover:bg-slate-100"
                  onClick={() => setSpotlightOpen(true)}
                  disabled={!selectedAccountId}
                >
                  <Search className="h-4 w-4" />
                </Button>

                <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                  <SelectTrigger className="h-10 min-w-[220px] text-base">
                    <SelectValue placeholder="Mandant wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem
                        key={account.id}
                        value={account.id}
                        className="cursor-pointer focus:bg-[#4189a8] focus:text-white"
                      >
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="lastschrift" className="mt-0">
            {selectedAccountId ? (
              <SepaTransactionTab
                key={`ls-${selectedAccountId}`}
                accountId={selectedAccountId}
                filters={{
                  direction: SepaDirectionEnum.DirectDebit,
                  status: SepaTransactionStatusEnum.Pending,
                }}
                selectable
                emptyText="Keine offenen Lastschriften für diesen Mandanten."
              />
            ) : (
              <TabPlaceholder title="Offene Lastschriften" account={selectedAccountName} />
            )}
          </TabsContent>
          <TabsContent value="rueckerstattungen" className="mt-0">
            {selectedAccountId ? (
              <SepaTransactionTab
                key={`ct-${selectedAccountId}`}
                accountId={selectedAccountId}
                filters={{
                  direction: SepaDirectionEnum.CreditTransfer,
                  status: SepaTransactionStatusEnum.Pending,
                }}
                selectable
                emptyText="Keine offenen Rückerstattungen für diesen Mandanten."
              />
            ) : (
              <TabPlaceholder title="Offene Rückerstattungen" account={selectedAccountName} />
            )}
          </TabsContent>
          <TabsContent value="zurueckgewiesen" className="mt-0">
            {selectedAccountId ? (
              <Tabs defaultValue="ls" className="w-full">
                <TabsList className="mb-3 inline-flex h-9 gap-1 rounded-lg bg-slate-100 p-1">
                  <TabsTrigger
                    value="ls"
                    className="rounded-md px-3 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#8a4150] data-[state=active]:shadow-sm"
                  >
                    Lastschrift
                  </TabsTrigger>
                  <TabsTrigger
                    value="ct"
                    className="rounded-md px-3 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#8a4150] data-[state=active]:shadow-sm"
                  >
                    Rückerstattung
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="ls" className="mt-0">
                  <SepaTransactionTab
                    key={`rej-ls-${selectedAccountId}`}
                    accountId={selectedAccountId}
                    filters={{
                      direction: SepaDirectionEnum.DirectDebit,
                      status: SepaTransactionStatusEnum.Returned,
                    }}
                    selectable
                    reexportMode
                    emptyText="Keine zurückgewiesenen Lastschriften für diesen Mandanten."
                  />
                </TabsContent>
                <TabsContent value="ct" className="mt-0">
                  <SepaTransactionTab
                    key={`rej-ct-${selectedAccountId}`}
                    accountId={selectedAccountId}
                    filters={{
                      direction: SepaDirectionEnum.CreditTransfer,
                      status: SepaTransactionStatusEnum.Returned,
                    }}
                    selectable
                    reexportMode
                    emptyText="Keine zurückgewiesenen Rückerstattungen für diesen Mandanten."
                  />
                </TabsContent>
              </Tabs>
            ) : (
              <TabPlaceholder title="Zurückgewiesene Transaktionen" account={selectedAccountName} />
            )}
          </TabsContent>
          <TabsContent value="alle" className="mt-0">
            {selectedAccountId ? (
              <SepaTransactionTab
                key={`all-${selectedAccountId}`}
                accountId={selectedAccountId}
                showExportColumn
                keyboardNav
                showText
                onOpenBatch={openBatchInExporte}
                emptyText="Keine SEPA-Transaktionen für diesen Mandanten."
              />
            ) : (
              <TabPlaceholder title="Alle SEPA-Transaktionen" account={selectedAccountName} />
            )}
          </TabsContent>
          <TabsContent value="exporte" className="mt-0">
            {selectedAccountId ? (
              <SepaExportBatchTab
                key={`ex-${selectedAccountId}`}
                accountId={selectedAccountId}
                onOpenTimeline={setTimelineBatchId}
                highlightBatchId={highlightBatch?.id ?? null}
                highlightNonce={highlightBatch?.nonce}
              />
            ) : (
              <TabPlaceholder title="Exporte" account={selectedAccountName} />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>

      {selectedAccountId && (
        <SepaSpotlightSearch
          accountId={selectedAccountId}
          open={spotlightOpen}
          onOpenChange={setSpotlightOpen}
          onOpenBatch={openBatchInExporte}
        />
      )}

      <SepaExportBatchTimelineDialog
        batchId={timelineBatchId}
        open={!!timelineBatchId}
        onOpenChange={(open) => !open && setTimelineBatchId(null)}
      />
    </Card>
  );
};

/** Temporary F2.0 placeholder; replaced by the real tables in F2.1+. */
const TabPlaceholder: React.FC<{ title: string; account?: string }> = ({ title, account }) => (
  <div className="rounded-lg border border-dashed border-slate-200 py-12 text-center">
    <p className="text-sm font-medium text-slate-600">{title}</p>
    <p className="mt-1 text-sm text-slate-400">
      {account ? `Mandant: ${account}` : "Bitte einen Mandanten wählen."}
    </p>
  </div>
);

export default SepaExportManager;
